import {
  type DashboardSearch,
  dashboardSearchFrom,
  isDefaultDashboardSearch,
} from '@/components/dashboard-filters/dashboard-period'

/** Orders: confirmed sales orders. Invoices: posted invoices and cash sales. */
export type SalesBasis = 'orders' | 'invoices'

/**
 * The Sales dashboard's URL search: the shared filters plus `basis`. Orders
 * is the default and never written to the URL; only `basis=invoices` is.
 */
export interface SalesDashboardSearch extends DashboardSearch {
  basis?: 'invoices'
}

export function salesDashboardSearchFrom(search: Record<string, unknown>): SalesDashboardSearch {
  return {
    ...dashboardSearchFrom(search),
    basis: search.basis === 'invoices' ? 'invoices' : undefined,
  }
}

/**
 * The basis the screen asks for. Invoices needs `accounting_invoice.view` (the
 * API answers 403 without it), so a reader without it always reads Orders,
 * whatever the URL says. With no basis in the URL, the screen's own default
 * applies: `defaultToInvoices` is true only when the period has no orders but
 * has invoices, and an explicit `basis` in the URL always wins over it.
 */
export function effectiveBasis(
  search: SalesDashboardSearch,
  canReadInvoices: boolean,
  defaultToInvoices = false,
): SalesBasis {
  if (!canReadInvoices) return 'orders'
  if (search.basis === 'invoices') return 'invoices'
  return search.basis === undefined && defaultToInvoices ? 'invoices' : 'orders'
}

/** Whether every filter, the basis included, is at its default. */
export function isDefaultSalesSearch(search: SalesDashboardSearch, basis: SalesBasis): boolean {
  return isDefaultDashboardSearch(search) && basis === 'orders'
}
